import { Avatar, Chip, CircularProgress, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { tokens, fontSize } from "@utils/tokens";

export type ChipSelectorItem = { id: string; label: string };

export type ChipSelectorStripProps = {
  items: ChipSelectorItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  loading?: boolean;
  emptyText?: string;
  ariaLabel?: string;
  /** Stable test-id prefix; each chip gets `${testIdPrefix}-${id}`. */
  testIdPrefix?: string;
  /** Per-item avatar colour (e.g. hgColor by id). Defaults to the theme primary. */
  getColor?: (item: ChipSelectorItem, index: number) => string;
};

/**
 * Horizontal swipe-strip of selectable chips — the grade-page child-selector
 * pattern, generalised. One row that scrolls sideways on a phone instead of
 * wrapping; the selected chip is filled with a soft primary tint. Theme-aware
 * (works in light + dark). Use for selecting among a SMALL set of entities the
 * user owns (their children/students), NOT for large lists.
 */
export default function ChipSelectorStrip({
  items,
  selectedId,
  onSelect,
  loading = false,
  emptyText = "No options",
  ariaLabel = "Select",
  testIdPrefix,
  getColor,
}: ChipSelectorStripProps) {
  if (loading) {
    return (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" sx={{ color: "text.secondary" }}>Loading…</Typography>
      </Stack>
    );
  }
  if (items.length === 0) {
    return <Typography variant="body2" sx={{ color: "text.secondary" }}>{emptyText}</Typography>;
  }
  return (
    <Stack
      direction="row"
      spacing={1}
      role="group"
      aria-label={ariaLabel}
      sx={{ overflowX: "auto", pb: 0.5, scrollbarWidth: "none", "&::-webkit-scrollbar": { display: "none" } }}
    >
      {items.map((item, i) => {
        const selected = item.id === selectedId;
        return (
          <Chip
            key={item.id}
            data-testid={testIdPrefix ? `${testIdPrefix}-${item.id}` : undefined}
            avatar={
              <Avatar sx={{ bgcolor: getColor ? getColor(item, i) : "primary.main", color: tokens.white, fontSize: fontSize.xs, fontWeight: 700 }}>
                {item.label.slice(0, 1)}
              </Avatar>
            }
            label={item.label}
            onClick={() => onSelect(item.id)}
            aria-pressed={selected}
            variant={selected ? "filled" : "outlined"}
            sx={{
              flexShrink: 0,
              ...(selected
                ? { bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12), color: "primary.main", fontWeight: 600 }
                : {}),
            }}
          />
        );
      })}
    </Stack>
  );
}
